import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  useInput,
} from '@met/react-components';
import { StyledModalHeader, StyledErrorMessage } from '../TestCardDialog/styled';
import { BillingCodeInput, CardholderInput, FormColumn } from './styled';
import AdyenAddCreditCard from '../AdyenAddCreditCard';

const AddCreditCardDialog = React.memo(({
  hidden,
  toggle,
  appConfig,
  onCreditCardAdded,
  canSave,
  isLoading,
}) => {
  const cardholderName = useInput('');
  const billingPostalCode = useInput('');
  const [saveCard, setSaveCard] = useState(true);
  const [isAdyenValid, setIsAdyenValid] = useState(false);
  const [encryptedCard, setEncryptedCard] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const resetForm = useCallback(() => {
    cardholderName.clear();
    billingPostalCode.clear();
    setSaveCard(true);
    setIsAdyenValid(false);
    setEncryptedCard(null);
    setErrorMessage(null);
  }, [billingPostalCode, cardholderName]);

  useEffect(() => {
    if (hidden) {
      resetForm();
    }
  }, [hidden, resetForm]);

  const handleValidityChange = useCallback((isValid, paymentMethod) => {
    setIsAdyenValid(isValid);
    setEncryptedCard(isValid ? paymentMethod : null);
  }, []);

  const handleAddClicked = () => {
    if (!cardholderName.hasValue) {
      setErrorMessage('Please enter the cardholder name');
      return;
    }

    if (!billingPostalCode.hasValue) {
      setErrorMessage('Please enter the billing code');
      return;
    }

    if (!isAdyenValid || !encryptedCard) {
      setErrorMessage('Please complete all card fields');
      return;
    }

    setErrorMessage(null);

    onCreditCardAdded(
      { billing_postal_code: billingPostalCode.value.trim() },
      null,
      cardholderName.value.trim(),
      canSave ? saveCard : false,
      {
        encryptedCardNumber: encryptedCard.encryptedCardNumber,
        encryptedExpiryMonth: encryptedCard.encryptedExpiryMonth,
        encryptedExpiryYear: encryptedCard.encryptedExpiryYear,
        encryptedSecurityCode: encryptedCard.encryptedSecurityCode,
      }
    ).then(() => toggle());
  };

  const isActive = !hidden;

  return (
    <Modal centered hidden={hidden} toggle={toggle}>
      <ModalContent>
        <StyledModalHeader toggle={toggle}>
          <FontAwesomeIcon icon="credit-card" /> Add New card
        </StyledModalHeader>
        <ModalBody style={{ minHeight: '300px' }}>
          <FormColumn>
            <CardholderInput
              id="adyen-cardholder-name"
              label="Cardholder Name"
              placeholder="Cardholder Name"
              {...cardholderName.bindToInput}
            />
            {isActive && (
              <AdyenAddCreditCard
                clientKey={appConfig.adyenClientKey}
                environment={appConfig.adyenEnvironment || 'test'}
                cardBrands={appConfig.adyenCardBrands}
                isActive={isActive}
                onValidityChange={handleValidityChange}
              />
            )}
            <BillingCodeInput
              id="adyen-billing-postal-code"
              label="Billing code"
              placeholder="Billing code"
              {...billingPostalCode.bindToInput}
            />
            {canSave && (
              <Checkbox
                id="adyen-save-card"
                label="Save"
                checked={saveCard}
                onCheck={({ checked }) => setSaveCard(checked)}
              />
            )}
            {errorMessage && (
              <StyledErrorMessage>
                <FontAwesomeIcon icon="exclamation-circle" /> {errorMessage}
              </StyledErrorMessage>
            )}
          </FormColumn>
        </ModalBody>
        <ModalFooter>
          <Button primary outline onClick={toggle}>Cancel</Button>
          <Button
            primary
            onClick={handleAddClicked}
            loading={isLoading}
            disabled={!isAdyenValid}
          >
            Add Card
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

AddCreditCardDialog.propTypes = {
  hidden: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  appConfig: PropTypes.shape({
    adyenClientKey: PropTypes.string,
    adyenEnvironment: PropTypes.string,
    adyenCardBrands: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onCreditCardAdded: PropTypes.func.isRequired,
  canSave: PropTypes.bool,
  isLoading: PropTypes.bool,
};

AddCreditCardDialog.defaultProps = {
  hidden: true,
  canSave: false,
  isLoading: false,
};

export default AddCreditCardDialog;
